import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/lib/useProfile";
import type { ActivityLevel, DietPattern, Profile } from "@/types";

export default function Settings() {
  const { user, logout } = useAuth();
  const { profile, reload } = useProfile();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!profile) {
    return <div className="p-6 text-sm text-forest-500">Loading settings…</div>;
  }

  async function updateField<K extends keyof Profile>(key: K, value: Profile[K]) {
    setSaving(true);
    await api.profile.update({ [key]: value } as Partial<Omit<Profile, "userId">>);
    reload();
    setSaving(false);
  }

  return (
    <div className="mx-auto max-w-md px-4 pb-8 pt-[calc(env(safe-area-inset-top)+1.5rem)]">
      <h1 className="text-2xl font-semibold text-forest-700">Settings</h1>
      <p className="mt-1 text-sm text-forest-500">{user?.email}</p>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-ink">Profile</h2>
        <div className="divide-y divide-line rounded-card border border-line bg-white">
          <Row label="Activity level">
            <select
              className="bg-transparent text-sm text-forest-700"
              value={profile.activityLevel}
              disabled={saving}
              onChange={(e) => updateField("activityLevel", e.target.value as ActivityLevel)}
            >
              <option value="sedentary">Sedentary</option>
              <option value="light">Light</option>
              <option value="moderate">Moderate</option>
              <option value="active">Active</option>
            </select>
          </Row>
          <Row label="Dietary pattern">
            <select
              className="bg-transparent text-sm text-forest-700"
              value={profile.dietPattern}
              disabled={saving}
              onChange={(e) => updateField("dietPattern", e.target.value as DietPattern)}
            >
              <option value="omnivore">Omnivore</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="vegan">Vegan</option>
              <option value="pescatarian">Pescatarian</option>
            </select>
          </Row>
          <Row label="Gestational diabetes">
            <input
              type="checkbox"
              className="h-5 w-5 accent-forest-700"
              checked={profile.hasGestationalDiabetes}
              disabled={saving}
              onChange={(e) => updateField("hasGestationalDiabetes", e.target.checked)}
            />
          </Row>
          <Row label="Hypertension">
            <input
              type="checkbox"
              className="h-5 w-5 accent-forest-700"
              checked={profile.hasHypertension}
              disabled={saving}
              onChange={(e) => updateField("hasHypertension", e.target.checked)}
            />
          </Row>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-ink">Data</h2>
        <div className="flex flex-col gap-3">
          <a href={api.account.exportCsv()} className="btn-secondary text-center">
            Export data as CSV
          </a>
          <button className="btn-secondary" onClick={() => logout()}>
            Sign out
          </button>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-clay-500">Danger zone</h2>
        {!confirmingDelete ? (
          <button
            className="h-tap w-full rounded-pill border border-clay-500 px-6 text-sm font-medium text-clay-500"
            onClick={() => setConfirmingDelete(true)}
          >
            Delete account and all data
          </button>
        ) : (
          <div className="rounded-card border border-clay-500 bg-white p-4">
            <p className="text-sm text-ink">
              This permanently deletes your account and every log. This can't be undone.
            </p>
            <div className="mt-3 flex gap-3">
              <button className="btn-secondary" onClick={() => setConfirmingDelete(false)}>
                Cancel
              </button>
              <button
                className="h-tap flex-1 rounded-pill bg-clay-500 text-sm font-medium text-canvas"
                onClick={() => api.account.deleteAccount().then(() => logout())}
              >
                Yes, delete everything
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-tap items-center justify-between px-4">
      <span className="text-sm text-ink">{label}</span>
      {children}
    </div>
  );
}
